/**
 * Table columns for the SwitchPlansTrade unified table.
 *
 * Uses shared switch column definitions from shared/switchTableColumns.js
 * and adds switch-plan-specific columns (plan settings) and behaviors.
 */
import React from "react";
import _ from "lodash";

import { makeStyles } from "@material-ui/core";

import { CurrencyNumberFormat } from "../../../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement";
import { PercentageNumberFormat } from "../../../../../../RiskProfiling/components/StepContent/components/formElement/InputFormElement/InputFormElement";
import { NoChangeError } from "../../../../TradeStep/components/PortfolioTrade/table-data";
import SwitchQuantityField from "../../PortfolioTradeV2/components/SwitchSection/SwitchQuantityField";
import SavingPlanSettingsForTrading from "../../SavingsPlansTrade/components/SavingPlanSettingsForTrading";
import { getNumberValue } from "../../../../../utils";
import { withEuroOrDash } from "../../../../../../../utils/utils";

import {
  SWITCH_ROW_TYPE,
  SwitchPositionCol,
  SwitchSustainabilityCol,
  SwitchSRRICol,
  SwitchBrokerFeeCol,
  SwitchDiscountCol,
  SwitchActionsCol,
  createSwitchNameCol,
  createSwitchAmountEuroCol,
  createSwitchPercentageCol,
  switchInHeaderDistribute,
} from "../../shared/switchTableColumns";

// Re-export for consumers
export { SWITCH_ROW_TYPE };
export const SWITCH_PLAN_ROW_TYPE = SWITCH_ROW_TYPE;

const usePlanSettingsStyles = makeStyles((theme) => ({
  changedLabel: {
    color: theme.palette.primary.main,
  },
}));

// region SWITCH-PLAN-SPECIFIC COLUMNS

const SwitchPlanNameCol = createSwitchNameCol({
  onAddClick: (item, options) =>
    options.handleSwitchInAddClick(options.switchOutsMap[item.switchOutItemId]),
});

const SwitchPlanAmountEuroCol = createSwitchAmountEuroCol({
  switchOut: (item, options) => {
    const classes = usePlanSettingsStyles();
    const isChanged =
      item.action === "edit" && item.is_changed && _.get(item, "changes.transaction_value");

    return (
      <>
        <SwitchQuantityField
          value={getNumberValue(_.get(item, "calculated.transaction_value_euro"))}
          onChange={(value) => options.handleSwitchAmountEuroChanged(item, value)}
          error={_.get(item.errors, "transaction_value")}
          inputComponent={CurrencyNumberFormat}
          inputProps={{ allowNegative: false, fixedDecimalScale: true }}
          disabled={item.action === "delete"}
          inputRootClassName={isChanged ? classes.changedLabel : undefined}
          currentValueLabel={withEuroOrDash(item.data.market_value)}
        />
        <NoChangeError item={item} />
      </>
    );
  },
  switchInHeader: (item, options, switchOutItem) => {
    const transactionSaldo = options.getTransactionSaldo(switchOutItem);
    return (
      <SwitchQuantityField
        value={getNumberValue(transactionSaldo)}
        inputComponent={CurrencyNumberFormat}
        inputProps={{ suffix: "", placeholder: "", fixedDecimalScale: true, allowNegative: true }}
        inputRootClassName={transactionSaldo !== 0 ? "text-red" : "text-green"}
        disabled
      />
    );
  },
  switchInErrorField: 'transaction_value'
});

const SwitchPlanPercentageCol = createSwitchPercentageCol({
  header: () => <>Prozent Tausch<br />Ausführung</>,
  switchOut: (item, options) => {
    const classes = usePlanSettingsStyles();
    return (
      <>
        <SwitchQuantityField
          value={getNumberValue(_.get(item, "calculated.transaction_value_percentage"))}
          onChange={(value) => options.handleSwitchPercentageChanged(item, value)}
          error={_.get(item.errors, "transaction_value_percentage")}
          inputComponent={PercentageNumberFormat}
          inputProps={{ allowNegative: false }}
          disabled={item.action === "delete"}
        />
        <SavingPlanSettingsForTrading item={item} options={options} classes={classes} />
      </>
    );
  },
  switchInHeader: switchInHeaderDistribute,
});



const SwitchPlanSRRICol = {
  ...SwitchSRRICol,
  footer: [() => <div style={{ textAlign: "right" }}>Gesamt</div>],
};

// endregion

// region TABLE STRUCTURE

export const SWITCH_PLAN_TABLE_STRUCTURE = [
  SwitchPositionCol,
  SwitchPlanNameCol,
  SwitchSustainabilityCol,
  SwitchPlanSRRICol,
  SwitchPlanAmountEuroCol,
  SwitchPlanPercentageCol,
  SwitchBrokerFeeCol,
  SwitchDiscountCol,
  SwitchActionsCol,
];

// endregion
